import { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import BoutonNavigation from './BoutonNavigation';
import ThemeContext from './Theme';

function Header() {
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { theme, toggleTheme } = useContext(ThemeContext);

  const variantStyles = {
    light: {
      devis: 'bg-bleu-footer text-white border-white hover:bg-gris-clair hover:text-bleu-footer hover:border-bleu-footer',
      header: 'bg-white',
      logo: './images/logo_i2c_noir.svg',
      iconColor: 'text-bleu-footer',
    },
    dark: {
      devis: 'bg-white text-sky-800 border-bleu-footer hover:bg-bleu-footer hover:text-white hover:border-white',
      header: 'bg-bleu-footer',
      logo: './images/logo_i2c_blanc.svg',
      iconColor: 'text-white',
    },
  };

  const styles = variantStyles[theme] || variantStyles.light;

  const navItems = [
    { name: 'Accueil', lien: '/' },
    {
      name: 'Nos services',
      liste: [
        ['Ingénierie', '/ingenierie'],
        ['Conseil', '/conseil'],
        ['Cognitique', '/cognitique'],
      ],
    },
    {
      name: 'Qui sommes-nous',
      liste: [
        ['Notre mandat', '/mandat'],
        ['La CNJE', '/cnje'],
        ['L’ENSC', '/ensc'],
      ],
    },
    { name: 'Nous contacter', lien: '/contact' },
  ];

  const MenuDeroulant = ({ name, liste }) => {
    const [hidden, setHidden] = useState(true);
    const navigate = useNavigate();

    return (
      <div
        className="relative group"
        onMouseEnter={() => setHidden(false)}
        onMouseLeave={() => setHidden(true)}
      >
        <div className="flex items-center cursor-pointer select-none text-black dark:text-white transition-colors duration-300 ease-in-out">
          <button className="px-2">{name}</button>
          <img src="./icons/menu_item_arrow.svg" alt="Flèche" className="px-1 -rotate-90 group-hover:rotate-0 transition-transform duration-150 ease-linear"/>
        </div>
        {!hidden && (
          <div className="absolute top-full left-0 z-50 mt-0">
            <ul className="w-[187px] h-[150px] p-3 bg-gris-clair dark:bg-bleu-gris flex flex-col justify-around items-start rounded-2xl shadow-xl rounded-tl-none -translate-x-4 translate-y-3 text-black transition-colors duration-300 ease-in-out">
              {liste.map(([label, lien], idx) => (
                <li key={idx} onClick={() => navigate(lien)} className="px-4 py-2 hover:bg-blue-100 w-[162px] h-auto text-ms rounded-lg rounded-tl-0 cursor-pointer">{label}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  return (
    <header className={`${styles.header} py-5 transition-colors duration-300 ease-in-out`}>
      <div className="flex justify-between items-center px-4 md:px-10">
        <div className="w-20 h-20 flex items-center justify-center">
          {styles.logo ? (
            <embed src={styles.logo} alt="Logo d'i2c" className="w-full h-full" />
          ) : (
            <div className="w-full h-full bg-gray-200" />
          )}
        </div>

        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className={`lg:hidden ${styles.iconColor} focus:outline-none transition-colors duration-300 ease-in-out`}
          title="Ouvrir/fermer le menu mobile"
          aria-label="Ouvrir/fermer le menu mobile"
        >
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {isMobileMenuOpen ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            )}
          </svg>
        </button>

        <nav className="hidden lg:flex flex-row justify-center items-center gap-8 h-14">
          {navItems.map((item, idx) =>
            item.liste ? (
              <MenuDeroulant key={idx} name={item.name} liste={item.liste} />
            ) : (
              <BoutonNavigation key={idx} name={item.name} lien={item.lien} variant={theme} />
            )
          )}
          <div
            id="devis"
            className={`${styles.devis} py-4 px-6 rounded-lg border-2 hover:border-2 cursor-pointer transition-colors duration-300 ease-in-out`}
            onClick={() => navigate('/contact')}
          >
            <button>Devis</button>
          </div>
          <BoutonNavigation name="Espace membre" lien="/espace-membre" variant={theme} />
          <button 
            onClick={toggleTheme} 
            className="p-2 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 transition-colors duration-300 ease-in-out"
            aria-label="Changer le thème"
          >
            {theme === 'light' ? '🌙' : '☀️'}
          </button>
        </nav>
      </div>

      <div
        className={`lg:hidden mt-4 flex flex-col items-center space-y-3 overflow-hidden transition-all duration-300 ease-in-out ${
          isMobileMenuOpen ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        {navItems.map((item, idx) =>
          item.liste ? (
            <div key={idx} className="w-full flex flex-col items-center">
              <details className="w-full text-black dark:text-white">
                <summary className="cursor-pointer text-center py-2 font-semibold hover:text-sky-700 dark:hover:text-sky-300">{item.name}</summary>
                <ul className="flex flex-col bg-gray-100 dark:bg-bleu-gris w-full text-center rounded-lg mt-1 shadow-lg text-black dark:text-white">
                  {item.liste.map(([label, lien], subIdx) => (
                    <li key={subIdx} onClick={() => { navigate(lien); setIsMobileMenuOpen(false); }}className="py-2 border-b border-gray-200 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-600 cursor-pointer">{label}</li>
                  ))}
                </ul>
              </details>
            </div>
          ) : (
            <BoutonNavigation key={idx} name={item.name} lien={item.lien} variant={theme}/>
          )
        )}
        <div id="devis" className={`${styles.devis} py-4 px-6 rounded-lg border-2 hover:border-2 cursor-pointer transition-colors duration-300 ease-in-out`}
          onClick={() => { navigate('/contact'); setIsMobileMenuOpen(false); }}>
          <button>Devis</button>
        </div>
        <BoutonNavigation name="Espace membre" lien="/espace-membre" variant={theme} />
        <button 
          onClick={toggleTheme} 
          className="p-2 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 transition-colors duration-300 ease-in-out"
          aria-label="Changer le thème"
        >
          {theme === 'light' ? '🌙' : '☀️'}
        </button>
      </div>
    </header>
  );
}

export default Header;